import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { LocalUserRoles } from '@buspro/shared/common';
import { OperationsApi, OperationsDto } from '@buspro/web/apis';
import { ApiError } from '@buspro/web/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import moment from 'moment';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NGXLogger } from 'ngx-logger';
import { MomentModule } from 'ngx-moment';
import { NgxPermissionsModule, NgxPermissionsObject, NgxPermissionsService } from 'ngx-permissions';
import { lastValueFrom } from 'rxjs';
import { CollectingOperationsPanelComponent } from './collecting-operations-panel.component';

@Component({
  selector: 'buspro-admin-operations-ready-for-audit-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzSpinModule,
    NzAlertModule,
    NzToolTipModule,
    MomentModule,
    NgxPermissionsModule,
    CollectingOperationsPanelComponent,
  ],
  templateUrl: './operations-ready-for-audit-page.component.html',
  styleUrls: ['./operations-ready-for-audit-page.component.scss'],
})
export class OperationsReadyForAuditPageComponent {
  private operationsApi = inject(OperationsApi);
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);
  private modal = inject(NzModalService);
  private logger = inject(NGXLogger);
  private notification = inject(NzNotificationService);

  // Role constants for template
  LocalUserRoles = LocalUserRoles;
  adminRoles = [LocalUserRoles.LOCAL_ADMIN, LocalUserRoles.LOCAL_MANAGER];

  /**
   * Whether this user is an oversight role — Local Administrator or Local Manager.
   *
   * Read from the ngx-permissions store, NOT from `AdminRoleService.getCurrentUserRole()`.
   * That reads `LocalAuthService.currentUser$`, which this app does not actually hydrate:
   * the shell takes its user from `ngxAuthService.state` instead (see admin-sidenav and
   * admin-workspace-layout), so `currentUser$` can be null while the user is plainly
   * signed in — which silently made every admin look like an auditor here. The permissions
   * store is the source `*ngxPermissionsOnly` on this very page already relies on, so this
   * agrees with the rest of the page by construction.
   *
   * Mirrors AUDIT_OVERSIGHT_ROLES on the server, and governs both relaxations: starting
   * out of FIFO order, and holding more than one audit at a time. This only decides what
   * the page offers; the server re-checks the role from the token and is the real gate.
   */
  private loadedPermissions = toSignal(this.permissionsService.permissions$, {
    initialValue: {} as NgxPermissionsObject,
  });

  canOverrideAuditOrder = computed(() => {
    const loaded = this.loadedPermissions();
    return this.adminRoles.some((role) => role in loaded);
  });

  /** The user's role, for copy that names it ("As a Local Manager you may..."). */
  roleLabel = computed(() => {
    const loaded = this.loadedPermissions();
    return this.adminRoles.find((role) => role in loaded) ?? 'manager';
  });

  /** Operation the user asked to start ahead of the queue, awaiting confirmation. */
  overrideCandidate = signal<OperationsDto | null>(null);
  overrideReason = signal('');

  // Query to fetch operations ready for audit
  operationsQuery = injectQuery(() => ({
    queryKey: ['operations-ready-for-audit'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetOperationsReadyForAudit()),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  }));

  // Every audit this user currently holds open. Auditors can only ever have one; oversight
  // roles may have several, and all of them are listed back so none sits open unnoticed.
  auditsInProgressQuery = injectQuery(() => ({
    queryKey: ['my-audits-in-progress'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditsInProgress()),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  }));

  shouldShowAuditPolicy = computed(() => (this.operationsQuery.data()?.length ?? 0) >= 2);

  // Mutation to start audit process
  startAuditMutation = injectMutation(() => ({
    mutationFn: ({ operationId, reason }: { operationId: string; reason: string | null }) =>
      lastValueFrom(
        this.operationsApi.operationsControllerStartAuditProcess({
          id: operationId,
          body: reason ? { orderOverrideReason: reason } : {},
        }),
      ),
    onSuccess: (_data, { operationId }) => {
      this.notification.success('✅ Audit Started', 'Redirecting to audit screen...');

      // Refresh queries to update state
      this.operationsQuery.refetch();
      this.auditsInProgressQuery.refetch();

      // Auto-redirect to audit detail page
      setTimeout(() => {
        this.router.navigate(['/audits', operationId, 'detail']);
      }, 500);
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to start audit:', error);

      const apiError = error as ApiError;
      const message = apiError.error?.detail || 'Failed to start audit process. Please try again.';

      this.notification.error('❌ Cannot Start Audit', message);
    },
  }));

  /**
   * Release an audit back to the queue.
   *
   * Offered right here, on the page where a held audit is felt, rather than only on My
   * Audits — a stalled audit that can only be released somewhere else is how this page
   * stayed fully disabled for days.
   */
  cancelAuditMutation = injectMutation(() => ({
    mutationFn: (operationId: string) =>
      lastValueFrom(this.operationsApi.operationsControllerCancelAudit({ id: operationId })),
    onSuccess: () => {
      this.notification.success('Audit released', 'The operation is back in the queue.');
      this.operationsQuery.refetch();
      this.auditsInProgressQuery.refetch();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to cancel audit:', error);
      const apiError = error as ApiError;
      this.notification.error('Could not release audit', apiError.error?.detail || 'Please try again.');
    },
  }));

  releaseAudit(operation: OperationsDto): void {
    this.modal.confirm({
      nzTitle: 'Release this audit?',
      nzContent: `${operation.auditNumber} goes back into the queue for anyone to audit. Anything you already recorded on it is kept.`,
      nzOkText: 'Yes, release it',
      nzOkDanger: true,
      nzCancelText: 'Keep auditing',
      nzOnOk: () => {
        this.cancelAuditMutation.mutate(operation.id);
      },
    });
  }

  /** Audits this user currently holds open, oldest first. */
  auditsInProgress(): OperationsDto[] {
    return this.auditsInProgressQuery.data() ?? [];
  }

  hasAuditInProgress(): boolean {
    return this.auditsInProgress().length > 0;
  }

  /**
   * Whether an open audit stops this user starting another.
   *
   * Only Auditors are held to one at a time — it keeps the queue moving and stops half-done
   * audits piling up. For an oversight role it meant abandoning whatever they were reviewing
   * to look at one specific trip, which is how a single stalled audit blocked this entire
   * page for days.
   */
  blockedByOpenAudit(): boolean {
    return !this.canOverrideAuditOrder() && this.hasAuditInProgress();
  }

  /**
   * Check if an operation is the oldest (first in line for audit)
   */
  isOldestOperation(operation: OperationsDto): boolean {
    const ops = this.operationsQuery.data() || [];
    return ops.length > 0 && ops[0].id === operation.id;
  }

  /**
   * How many older operations are still waiting ahead of this one.
   * The list is server-ordered oldest-first, so the row's index is the skip count.
   */
  olderWaitingCount(operation: OperationsDto): number {
    const ops = this.operationsQuery.data() || [];
    const index = ops.findIndex((op) => op.id === operation.id);
    return index > 0 ? index : 0;
  }

  /**
   * Check if user can start audit for this operation
   *
   * Everyone is still held to one audit at a time. Queue order is a hard rule only for
   * Auditors — Admins and Managers may start any ready operation, and the server records
   * the skip against their name.
   */
  canStartAudit(operation: OperationsDto): boolean {
    if (this.startAuditMutation.isPending() || this.blockedByOpenAudit()) {
      return false;
    }
    return this.isOldestOperation(operation) || this.canOverrideAuditOrder();
  }

  /**
   * Get tooltip message for start audit button
   */
  getTooltip(operation: OperationsDto): string {
    if (this.startAuditMutation.isPending()) {
      return 'Processing...';
    }

    if (this.blockedByOpenAudit()) {
      const openAudit = this.auditsInProgress()[0];
      return `Complete your current audit (${openAudit?.auditNumber}) first`;
    }

    if (this.isOldestOperation(operation)) {
      return 'Start auditing this operation';
    }

    if (this.canOverrideAuditOrder()) {
      const skipped = this.olderWaitingCount(operation);
      return `Starts ahead of ${skipped} older operation${skipped === 1 ? '' : 's'} — the skip is recorded`;
    }

    const oldest = this.operationsQuery.data()?.[0];
    if (oldest?.auditNumber) {
      return `Please audit ${oldest.auditNumber} first (completed earlier)`;
    }

    return 'Operations must be audited in order';
  }

  /**
   * Get human-readable waiting time from auditReadyAt
   */
  getWaitingTime(readyAt: Date | string | null | undefined): string {
    if (!readyAt) {
      return 'Unknown';
    }
    return moment(readyAt).fromNow();
  }

  /**
   * Get waiting time badge color based on duration
   */
  getWaitingBadgeClass(readyAt: Date | string | null | undefined): string {
    if (!readyAt) {
      return 'normal';
    }

    const hoursSince = moment().diff(moment(readyAt), 'hours');

    if (hoursSince > 48) {
      return 'urgent'; // More than 2 days
    } else if (hoursSince > 24) {
      return 'warning'; // More than 1 day
    }

    return 'normal';
  }

  /**
   * Calculate days waiting for audit
   */
  getDaysWaiting(readyAt: Date | string | null | undefined): number {
    if (!readyAt) {
      return 0;
    }
    return Math.floor(moment().diff(moment(readyAt), 'days', true));
  }

  /**
   * Start audit for an operation
   *
   * Taking the head of the queue starts immediately. Skipping it asks for confirmation
   * first: the dialog says how many operations are being jumped and that the skip is
   * recorded, so an override is a deliberate act rather than a stray click.
   */
  startAudit(operationId: string): void {
    const operation = this.getOperationById(operationId);
    if (!operation || !this.canStartAudit(operation)) {
      return;
    }

    if (!this.isOldestOperation(operation)) {
      this.overrideReason.set('');
      this.overrideCandidate.set(operation);
      return;
    }

    this.startAuditMutation.mutate({ operationId, reason: null });
  }

  /** Confirm an out-of-order start, sending the reason the user typed (if any). */
  confirmOverride(): void {
    const operation = this.overrideCandidate();
    if (!operation) {
      return;
    }

    const reason = this.overrideReason().trim();
    this.overrideCandidate.set(null);
    this.startAuditMutation.mutate({ operationId: operation.id, reason: reason || null });
  }

  cancelOverride(): void {
    this.overrideCandidate.set(null);
    this.overrideReason.set('');
  }

  /**
   * Get operation by ID from current query data
   */
  private getOperationById(operationId: string): OperationsDto | undefined {
    const ops = this.operationsQuery.data() || [];
    return ops.find((op) => op.id === operationId);
  }

  /**
   * Refresh operations list
   */
  refresh(): void {
    this.operationsQuery.refetch();
  }
}
